import React, { useState } from 'react';
import DataList from '../DataList';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import '../../styles/LabTestRequestList.css';
import { useAuth } from '../../hooks/useAuth';
import BillSurgeryModal from './BillSurgeryModal';

const ProcedureList = ({ patient, admissionId }) => {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [billTarget, setBillTarget] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const canBillSurgery =
    hasPermission('/procedure-billing', 'View') ||
    (hasPermission('/patient-billing', 'Add') && hasPermission('/patient-billing', 'Edit'));
  const canPromptBill =
    hasPermission('/patient-billing', 'Add') && hasPermission('/patient-billing', 'Edit');

  const id = patient ? patient.patient_id : undefined;
  const title = patient
    ? `Procedures for ${patient.patient_first_name} ${patient.patient_last_name} - ${patient.patient_number}`
    : 'Procedures';

  // Initialize apiConfig using ApiConfigFactory with the patient id (if available)
  const apiConfig = new ApiConfigFactory('procedures', title, 'patient_id', id);

  apiConfig.defaultSortAsc = false;
  apiConfig.defaultSortFieldId = 'scheduled_date';
  apiConfig.path = '/procedures';

  // Disable adding when there is no reference patient (view-all mode)
  if (!id) {
    apiConfig.allowAdd = false;
  }

  // Add filter for admission if provided
  if (admissionId) {
    apiConfig.additionalParams = { admission_id: admissionId };
  }

  // Client-side validation function
  apiConfig.additionalValidation = async (formData) => {
    let error = {};
    
    // Required field validations
    if (!formData.patient_id) {
      error.message = 'Patient ID is required';
      error.isValid = false;
      return error;
    }
    
    if (!formData.procedure_name || !formData.procedure_name.trim()) {
      error.message = 'Procedure name is required';
      error.isValid = false;
      return error;
    }
    
    if (!formData.procedure_type) {
      error.message = 'Procedure type is required';
      error.isValid = false;
      return error;
    }
    
    if (!formData.setting) {
      error.message = 'Setting is required';
      error.isValid = false;
      return error;
    }
    
    // Validate procedure_type values
    if (formData.procedure_type && !['major', 'minor','intermediate'].includes(formData.procedure_type)) {
      error.message = 'Procedure type must be either "major", "minor", or "intermediate"';
      error.isValid = false;
      return error;
    }
    
    // Validate setting values
    if (formData.setting && !['inpatient', 'outpatient'].includes(formData.setting)) {
      error.message = 'Setting must be either "inpatient" or "outpatient"';
      error.isValid = false;
      return error;
    }

    if (!formData.scheduled_date) {
      error.message = 'Scheduled date is required';
      error.isValid = false;
      return error;
    }
    
    // Validate status values
    if (formData.status && !['planned', 'in_progress', 'completed', 'cancelled'].includes(formData.status)) {
      error.message = 'Status must be: planned, in_progress, completed, or cancelled';
      error.isValid = false;
      return error;
    }
    
    // Validate time logic
    if (formData.start_time && formData.end_time) {
      const startTime = new Date(formData.start_time);
      const endTime = new Date(formData.end_time);
      if (startTime >= endTime) {
        error.message = 'End time must be after start time';
        error.isValid = false;
        return error;
      }
    }
    
    // If we get here, validation passed
    error.isValid = true;
    return error;
  };

  apiConfig.onUpdateSuccess = (formData) => {
    if (
      canPromptBill &&
      formData.status === 'completed' &&
      formData.billing_status !== 'paid'
    ) {
      if (window.confirm('Bill this surgery now?')) {
        setBillTarget(formData);
      }
    }
  };

  // Configure fields
  apiConfig
    .addField({
      name: 'procedure_id',
      label: 'Surgery ID',
      type: 'text',
      identifier: true,
      disabled: true,
      tab: 'Main'
    })
    .addField({
      name: 'patient_id',
      label: 'Patient ID',
      type: 'text',
      required: true,
      tab: 'Main',
      defaultValue: id,
      visible: false
    })
    .addField({
      name: 'admission_id',
      label: 'Admission',
      type: 'dropdown',
      required: false,
      tab: 'Main',
      defaultValue: admissionId
    })
    .addField({
      name: 'procedure_name',
      label: 'Surgery Name',
      type: 'text',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'procedure_type',
      label: 'Type',
      type: 'dropdown',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'setting',
      label: 'Setting',
      type: 'dropdown',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'status',
      label: 'Status',
      type: 'dropdown',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'scheduled_date',
      label: 'Scheduled Date',
      type: 'datetime-local',
      required: true,
      tab: 'Main'
    })
    /*
    .addField({
      name: 'start_time',
      label: 'Start Time',
      type: 'datetime-local',
      required: false,
      tab: 'Procedure Times'
    })
    .addField({
      name: 'end_time',
      label: 'End Time',
      type: 'datetime-local',
      required: false,
      tab: 'Procedure Times'
    })
    */
    .addField({
      name: 'location',
      label: 'Location',
      type: 'dropdown',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'primary_surgeon',
      label: 'Primary Surgeon',
      type: 'dropdown',
      required: true,
      tab: 'Main'
    })
    .addField({
      name: 'assisting_staff',
      label: 'Assisting Staff',
      type: 'textarea',
      required: false,
      tab: 'Main'
    });
    /*.addField({
      name: 'summary',
      label: 'Summary',
      type: 'textarea',
      required: false,
      tab: 'Details'
    })
    .addField({
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      tab: 'Details'
    });*/

  // Add static dropdown options
  apiConfig
    .addStaticDropdownField({
      name: 'procedure_type',
      options: [
        { id: 'major', name: 'Major Surgery' },
        { id: 'minor', name: 'Minor Surgery' },
        { id: 'intermediate', name: 'Intermediate Surgery' }
      ]
    })
    .addStaticDropdownField({
      name: 'setting',
      options: [
        { id: 'inpatient', name: 'Inpatient' },
        { id: 'outpatient', name: 'Outpatient' }
      ]
    })
    .addStaticDropdownField({
      name: 'status',
      options: [
        { id: 'planned', name: 'Planned' },
        { id: 'in_progress', name: 'In Progress' },
        { id: 'completed', name: 'Completed' },
        { id: 'cancelled', name: 'Cancelled' }
      ]
    })
      .addStaticDropdownField({
      name: 'location',
      options: [
        { id: 'pultney', name: 'Pultney' },
        { id: 'kissy', name: 'Kissy' }
      ]
    })
    .addStaticDropdownField({
      name: 'ward',
      options: [
        { id: 'Children', name: 'Children' },
        { id: 'Adult - Male', name: 'Adult - Male' },
        { id: 'Adult - Female', name: 'Adult - Female' },
        { id: 'Maternity', name: 'Maternity' },
        { id: 'ICU', name: 'ICU' },
        { id: 'Private - Male', name: 'Private - Male' },
        { id: 'Private - Female', name: 'Private - Female' }
      ]
    })
    .addDropdownField({
      name: 'admission_id',
      endpoint: id
        ? `/hospital-portal/api/patients/admissions/?patient_id=${id}&active=true`
        : '/hospital-portal/api/patients/admissions/?active=true',
      valueField: 'admission_id',
      labelField: 'admission_id',
      transformResponse: (data) => (Array.isArray(data) ? data : []).map((admission) => {
        const patient = admission.Patient || {};
        const name = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim() || 'Patient';
        return {
          value: admission.admission_id,
          label: `${name} — ${admission.admission_id}`,
        };
      }),
    })
    .addDropdownField({
      name: 'primary_surgeon',
      endpoint: '/hospital-portal/api/employees/doctors',
      valueField: 'doctor_name',
      labelField: 'doctor_name',
    });

  // Add billing fields for the Bill tab
  apiConfig
    .addField({
      name: 'billing_details',
      label: 'Billing Items',
      type: 'subform',
      required: false,
      tab: 'Pricing',
      fixedRows: true,
      defaultValue: [
        { item_id: 'surgeon_fees', item_name: 'Surgeon Fees', agreed_amount: 0 },
        { item_id: 'medicines_labs', item_name: 'Medicines + Labs', agreed_amount: 0 },
        { item_id: 'theater_fees', item_name: 'Theater Fees', agreed_amount: 0 },
        { item_id: 'anaesthetic_fees', item_name: 'Anaesthetic Fees', agreed_amount: 0 },
        { item_id: 'admission_fees', item_name: 'Admission Fees', agreed_amount: 0 }
      ],
      subFields: [
        {
          name: 'item_name',
          label: 'Billing Item',
          type: 'text',
          width: '200px',
          disabled: true
        },
        {
          name: 'agreed_amount',
          label: 'Agreed Amount ($)',
          type: 'number',
          width: '150px',
          required: false,
          step: '0.01',
          min: '0'
        }
      ],
      onChange: (value, formData, setFormData) => {
        // Ensure default billing items are always present
        const defaultItems = [
          { item_id: 'surgeon_fees', item_name: 'Surgeon Fees' },
          { item_id: 'medicines_labs', item_name: 'Medicines + Labs' },
          { item_id: 'theater_fees', item_name: 'Theater Fees' },
          { item_id: 'anaesthetic_fees', item_name: 'Anaesthetic Fees' },
          { item_id: 'admission_fees', item_name: 'Admission Fees' }
        ];

        // Merge with existing amounts or set to 0
        const billingDetails = defaultItems.map(defaultItem => {
          const existingItem = value.find(v => v.item_id === defaultItem.item_id);
          return {
            ...defaultItem,
            agreed_amount: existingItem ? existingItem.agreed_amount : 0
          };
        });

        // Calculate total price
        const totalPrice = billingDetails.reduce((sum, item) => {
          const amount = parseFloat(item.agreed_amount) || 0;
          return sum + amount;
        }, 0);

        setFormData({ 
          ...formData, 
          billing_details: billingDetails,
          total_price: totalPrice.toFixed(2)
        });
      },
      onInitialize: (value, data) => {
        // Calculate total on form initialization
        if (Array.isArray(value) && value.length > 0) {
          const totalPrice = value.reduce((sum, item) => {
            const amount = parseFloat(item.agreed_amount) || 0;
            return sum + amount;
          }, 0);
          
          // Set the total_price in the data object
          data.total_price = totalPrice.toFixed(2);
        }
        return value;
      }
    })
    .addField({
      name: 'total_price',
      label: 'Total Agreed Price ($)',
      type: 'text',
      required: false,
      tab: 'Pricing',
      disabled: true,
      defaultValue: '0.00'
    })
    .addField({
      name: 'sales_number',
      label: 'Invoice Number',
      type: 'text',
      required: false,
      tab: 'Pricing',
      disabled: true,
      visibleWhen: { field: 'billing_status', equals: 'paid' }
    })
    .addField({
      name: 'admission_days',
      label: 'Admission Days',
      type: 'number',
      required: false,
      tab: 'Pricing',
      min: '0',
      step: '1'
    })
    .addField({
      name: 'ward',
      label: 'Ward',
      type: 'dropdown',
      required: false,
      tab: 'Pricing'
    })
    .addField({
      name: 'assumptions',
      label: 'Notes',
      type: 'textarea',
      required: false,
      tab: 'Pricing',
      maxRows: 3
    })
    .addField({
      name: 'consentForm',
      label: 'Consent Form',
      type: 'file',
      required: false,
      tab: 'Files'
    })
    .addField({
      name: 'doctorReport',
      label: 'Doctor\'s Report',
      type: 'file',
      required: false,
      tab: 'Files'
    })
    .addField({
      name: 'otherForms',
      label: 'Other Forms',
      type: 'file',
      required: false,
      tab: 'Files'
    });

  // Add tabs
  apiConfig
    .addTab({ name: 'Main', roles: 'all', default: true })
    // .addTab({ name: 'Details', roles: 'all' })
    // .addTab({ name: 'Procedure Times', roles: 'Doctor,Nurse,Admin' })
    .addTab({ name: 'Pricing', securityPath: '/procedure-pricing', permission: 'View' })
    .addTab({ name: 'Files', securityPath: '/procedure-files', permission: 'View' });

  apiConfig.addTabAlert({
    tab: 'Main',
    className: 'form-tab-alert--warning',
    getMessage: (formData) => {
      if (formData.setting === 'inpatient' && !formData.admission_id) {
        return 'Admission is needed for billing. Select an active admission so this surgery can be billed with the admission invoice.';
      }
      return null;
    }
  });

  // Add columns
  apiConfig
    .addColumn({
      id: 'procedure_id',
      name: 'ID',
      selector: (row) => row.procedure_id,
      type: 'text',
      identifier: true,
      width: '75px',
      filterable: true,
      filterSelector: 'procedure_id',
      sortable: true
    });

  if (hasPermission('/patient-card', 'View') && !patient) {
    apiConfig.addColumn({
      name: 'Patient Number',
      type: 'link',
      label: (row) => row.Patient?.patient_number,
      path: '/patient-card',
      getState: (row) => ({ patient: row.Patient }),
      selector: (row) => row.Patient?.patient_number,
      sortable: true,
      filterable: true,
      filterSelector: 'Patient.patient_number',
      width: '120px'
    });
  }

  if (hasPermission('/patient-card', 'View') && !patient) {
    apiConfig.addColumn({
      name: 'Patient Name',
      selector: (row) => `${row.Patient?.patient_first_name || ''} ${row.Patient?.patient_last_name || ''}`,
      sortable: false,
      filterable: true,
      filterSelector: 'Patient.patient_first_name',
      width: '180px'
    });
  }

  apiConfig
    .addColumn({
      name: 'Surgery',
      selector: (row) => row.procedure_name,
      sortable: true,
      filterable: true,
      filterSelector: 'procedure_name',
      width: '200px'
    })
    .addColumn({
      name: 'Type',
      selector: (row) => row.procedure_type,
      sortable: true,
      filterable: true,
      filterSelector: 'procedure_type',
      width: '100px'
    })
    .addColumn({
      name: 'Setting',
      selector: (row) => row.setting,
      sortable: true,
      filterable: true,
      filterSelector: 'setting',
      width: '100px'
    })
    .addColumn({
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      filterable: true,
      filterSelector: 'status',
      width: '100px'
    })
    .addColumn({
      name: 'Billing',
      selector: (row) => (row.billing_status === 'paid' ? 'Paid' : 'Unpaid'),
      sortable: true,
      filterable: true,
      filterSelector: 'billing_status',
      width: '160px',
      cell: (row) => {
        const paid = row.billing_status === 'paid';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: paid ? '#276749' : '#b7791f', fontWeight: 600 }}>
              {paid ? 'Paid' : 'Unpaid'}
            </span>
            {!paid && row.status !== 'cancelled' && canBillSurgery && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setBillTarget(row);
                }}
                style={{
                  padding: '2px 8px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Bill Surgery
              </button>
            )}
          </div>
        );
      }
    })
    .addColumn({
      name: 'Scheduled Date',
      selector: (row) => row.scheduled_date,
      type: 'datetime',
      sortable: true,
      width: '160px'
    })
    .addColumn({
      name: 'Primary Surgeon',
      selector: (row) => row.primary_surgeon,
      sortable: true,
      filterable: true,
      filterSelector: 'primary_surgeon',
      width: '150px'
    })
    .addColumn({
      name: 'Consent',
      selector: (row) => row.consentForm ? '\u2713' : '\u2717 Missing',
      cell: (row) => {
        if (row.consentForm) {
          return <span style={{ color: '#28a745', fontWeight: 'bold' }}>{'\u2713'}</span>;
        }
        return (
          <span style={{ color: '#dc3545', fontWeight: 'bold' }} title="Consent form not uploaded">
            {'\u26a0'} Missing
          </span>
        );
      },
      sortable: false,
      width: '100px'
    });

  // Temporarily hidden — restore when procedure view / questionnaire are in use.
  // if (hasPermission('/procedure-view', 'View')) {
  //   apiConfig.addColumn({
  //     name: 'Actions',
  //     type: 'link',
  //     label: 'View Details',
  //     path: '/procedure-view',
  //     getState: (row) => ({
  //       procedure: row,
  //       patient: patient || row.Patient
  //     }),
  //     width: '120px'
  //   });
  // }

  // if (hasPermission('/procedure-form', 'Edit')) {
  //   apiConfig.addColumn({
  //     name: 'Questionnaire',
  //     type: 'link',
  //     label: (row) => row.questionnaire_data ? 'Edit Form' : 'Fill Form',
  //     path: '/procedure-form',
  //     getState: (row) => ({
  //       procedure: row,
  //       patient: patient || row.Patient,
  //       mode: 'questionnaire'
  //     }),
  //     visible: (row) => row.status !== 'cancelled',
  //     width: '120px'
  //   });
  // }

  // Highlight rows with missing consent form
  apiConfig.conditionalRowStyles = [
    {
      when: (row) => !row.consentForm,
      style: {
        backgroundColor: '#fff8e1',
        borderLeft: '3px solid #ff9800',
      },
    },
  ];

  return (
    <div className="patient-list-container">
      {patient && hasPermission('/procedure-form', 'Add') && (
        <div className="lab-request-link">
          <Link to="/procedure-form" state={{ patient, admissionId }}>
            Create New Procedure
          </Link>
        </div>
      )}
      <DataList key={listRefreshKey} apiConfig={apiConfig} columns={apiConfig.columns} />
      {billTarget && (
        <BillSurgeryModal
          procedure={billTarget}
          onClose={() => setBillTarget(null)}
          onBilled={() => {
            setBillTarget(null);
            setListRefreshKey((key) => key + 1);
          }}
        />
      )}
    </div>
  );
};

export default ProcedureList;
